// src/components/NewTextComponentForm.js
import React, { useEffect, useMemo, useState } from 'react';

export default function NewTextComponentForm({
                                                 sections = [],
                                                 onSectionsChange,

                                                 // submit/cancel
                                                 onSubmit,
                                                 onCancel,
                                                 displayMode = 'full', // 'full' | 'compressed'
                                                 onDisplayModeChange,
                                                 enablePublishButton = false,
                                                 onSubmitAndPublish,

                                                 // optional parent control of content type
                                                 variant, // 'text' | 'list' | 'link' | 'video'
                                                 onVariantChange,
                                             }) {
    // === Editing state ===
    const [editingIndex, setEditingIndex] = useState(null);

    // one draft section at a time
    const [h, setH] = useState('');
    const [sh, setSH] = useState('');
    const [body, setBody] = useState('');

    // image controls
    const [imgFile, setImgFile] = useState(null);
    const [imgPreview, setImgPreview] = useState('');
    const [imgUrlExisting, setImgUrlExisting] = useState(''); // existing url when editing
    const [imgMarkedClear, setImgMarkedClear] = useState(false); // "remove file" toggle

    // video controls
    const [videoTitle, setVideoTitle] = useState('');
    const [videoDescription, setVideoDescription] = useState('');
    const [videoFile, setVideoFile] = useState(null);
    const [videoUrlExisting, setVideoUrlExisting] = useState(''); // existing url when editing
    const [videoMarkedClear, setVideoMarkedClear] = useState(false); // "remove file" toggle

    // controlled/uncontrolled variant support
    const [draftType, setDraftType] = useState(variant || 'text');
    const isControlled = typeof variant === 'string';
    useEffect(() => {
        if (isControlled) setDraftType(variant);
    }, [isControlled, variant]);
    const setMode = (next) => {
        onVariantChange?.(next);
        if (!isControlled) setDraftType(next);
    };

    // video size cap (60 MB)
    const MAX_VIDEO_BYTES = 60 * 1024 * 1024;

    // draft presence / any data flags
    const hasDraft =
        draftType === 'video'
            ? !!(
                videoFile ||
                (videoTitle || '').trim() ||
                (videoDescription || '').trim() ||
                videoUrlExisting
            )
            : !!(
                h ||
                sh ||
                body ||
                imgFile ||
                imgUrlExisting
            );

    const hasAnyData = hasDraft || (sections && sections.length > 0);

    // preview lifecycle for image only (new files)
    useEffect(() => {
        if (!imgFile) {
            if (imgPreview) URL.revokeObjectURL(imgPreview);
            setImgPreview('');
            return;
        }
        const url = URL.createObjectURL(imgFile);
        setImgPreview(url);
        return () => URL.revokeObjectURL(url);
    }, [imgFile]); // eslint-disable-line react-hooks/exhaustive-deps

    // Build a draft section object (or null if truly empty)
    const buildDraftSection = () => {
        // VIDEO
        if (draftType === 'video') {
            const title = (videoTitle || '').trim();
            const description = (videoDescription || '').trim();

            // skip if absolutely nothing provided AND no existing url (edit)
            if (!videoFile && !title && !description && !videoUrlExisting) return null;

            if (videoFile && videoFile.size > MAX_VIDEO_BYTES) {
                alert('Video is over 60 MB. Please choose a file under 60 MB.');
                return null;
            }

            // parent will upload videoFile and replace with videoUrl on save
            // Preserve old url unless explicitly cleared or replaced
            let nextVideoUrl = videoUrlExisting || '';
            if (videoMarkedClear) nextVideoUrl = '';
            if (videoFile) nextVideoUrl = ''; // will be replaced by parent after upload

            return {
                type: 'video',
                title: title || undefined,
                description: description || undefined,
                // transport both url/file so parent can act accordingly
                videoUrl: nextVideoUrl || undefined,
                videoFile: videoFile || null,
                _clearVideo: videoMarkedClear || false, // hint to parent if you need it
            };
        }

        // TEXT/LIST/LINK
        const base = {
            heading: (h || '').trim(),
            subheading: (sh || '').trim(),
        };

        const bodyTrim = (body || '').trim();

        const isEmpty =
            !base.heading &&
            !base.subheading &&
            !imgFile &&
            !imgUrlExisting &&
            !bodyTrim;

        if (isEmpty) return null;

        // carry image url unless cleared or replaced
        let nextFeatureImageUrl = imgUrlExisting || '';
        if (imgMarkedClear) nextFeatureImageUrl = '';
        if (imgFile) nextFeatureImageUrl = ''; // will be replaced after upload

        const withImage = {
            ...base,
            featureImageUrl: nextFeatureImageUrl || undefined,
            featureImageFile: imgFile || null,
            _clearImage: imgMarkedClear || false, // hint to parent if you need it
        };

        if (draftType === 'list') {
            const items = bodyTrim.split('\n').map((s) => s.trim()).filter(Boolean);
            return { ...withImage, type: 'list', items };
        }

        if (draftType === 'link') {
            return {
                ...withImage,
                type: 'link',
                linkTitle: base.heading || undefined,
                linkUrl: bodyTrim || undefined, // body holds URL in link mode
            };
        }

        // default: text
        return {
            ...withImage,
            type: 'text',
            text: body,
        };
    };

    // Merge helper (for "Save Panel" and "Save & Publish")
    const mergedSections = useMemo(() => {
        const draft = buildDraftSection();
        if (!draft) return sections || [];

        if (editingIndex != null) {
            // replace the section being edited
            const copy = [...(sections || [])];
            copy[editingIndex] = draft;
            return copy;
        }
        // append new
        return [...(sections || []), draft];
    }, [
        sections,
        editingIndex,
        h,
        sh,
        body,
        imgFile,
        imgUrlExisting,
        imgMarkedClear,
        draftType,
        videoTitle,
        videoDescription,
        videoFile,
        videoUrlExisting,
        videoMarkedClear,
    ]);

    // Reset draft fields
    const resetDraft = () => {
        setH('');
        setSH('');
        setBody('');
        setImgFile(null);
        setImgUrlExisting('');
        setImgMarkedClear(false);
        setVideoTitle('');
        setVideoDescription('');
        setVideoFile(null);
        setVideoUrlExisting('');
        setVideoMarkedClear(false);
        setEditingIndex(null);
        if (!isControlled) setDraftType('text');
    };

    // Enter edit mode for section i (prefill all draft fields)
    const editSection = (i) => {
        const s = sections[i];
        if (!s) return;

        setEditingIndex(i);

        if (s.type === 'video') {
            setMode('video');
            setVideoTitle(s.title || '');
            setVideoDescription(s.description || '');
            setVideoFile(null);
            setVideoUrlExisting(s.videoUrl || '');
            setVideoMarkedClear(false);
            // clear text fields
            setH('');
            setSH('');
            setBody('');
            setImgFile(null);
            setImgUrlExisting('');
            setImgMarkedClear(false);
        } else {
            // text/list/link share these fields
            const head = s.type === 'link' ? (s.linkTitle || '') : (s.heading || '');
            setMode(s.type === 'list' ? 'list' : s.type === 'link' ? 'link' : 'text');
            setH(head);
            setSH(s.subheading || '');
            if (s.type === 'list') {
                setBody(Array.isArray(s.items) ? s.items.join('\n') : '');
            } else if (s.type === 'link') {
                setBody(s.linkUrl || '');
            } else {
                setBody(s.text || '');
            }
            setImgFile(null);
            setImgUrlExisting(s.featureImageUrl || '');
            setImgMarkedClear(false);

            // clear video fields
            setVideoTitle('');
            setVideoDescription('');
            setVideoFile(null);
            setVideoUrlExisting('');
            setVideoMarkedClear(false);
        }
    };

    // Add Section (for composing multiple sections before saving)
    const addSection = () => {
        const draft = buildDraftSection();
        if (!draft) return; // nothing to add
        // If editing, behave like "Save Changes" into the sections array
        if (editingIndex != null) {
            const copy = [...(sections || [])];
            copy[editingIndex] = draft;
            onSectionsChange(copy);
            resetDraft();
            return;
        }
        // else push new
        onSectionsChange([...(sections || []), draft]);
        // reset only new-draft fields
        resetDraft();
    };

    const removeSection = (idx) =>
        onSectionsChange(sections.filter((_, i) => i !== idx));

    const moveUp = (idx) => {
        if (idx <= 0) return;
        const copy = [...sections];
        [copy[idx - 1], copy[idx]] = [copy[idx], copy[idx - 1]];
        onSectionsChange(copy);
    };

    const moveDown = (idx) => {
        if (idx >= sections.length - 1) return;
        const copy = [...sections];
        [copy[idx + 1], copy[idx]] = [copy[idx], copy[idx + 1]];
        onSectionsChange(copy);
    };

    // SAVE: includes the current draft (new or edited)
    const handleSavePanel = () => {
        onSubmit?.(mergedSections);
        // keep editing state so parent can upload; do NOT reset here
    };

    // SAVE & PUBLISH
    const handleSaveAndPublish = () => {
        onSubmitAndPublish?.(mergedSections);
        // keep editing state so parent can upload; do NOT reset here
    };

    // === UI ===
    return (
        <div className="new-component-form">
            <h1>Text Component Form</h1>
            <h3>
                Sections ({sections.length})
                {editingIndex != null && (
                    <span style={{ marginLeft: 8, fontSize: 14, color: '#888' }}>
            • Editing section #{editingIndex + 1}
          </span>
                )}
            </h3>

            {/* Existing sections preview */}
            {sections.map((s, i) => (
                <div
                    key={i}
                    style={{
                        border: '1px solid #ddd',
                        padding: 8,
                        marginBottom: 8,
                        borderRadius: 6,
                        background: i === editingIndex ? 'rgba(0,0,0,0.04)' : 'transparent',
                    }}
                >
                    {/* VIDEO preview */}
                    {s.type === 'video' ? (
                        <>
                            {s.title && <h4 style={{ margin: '4px 0' }}>{s.title}</h4>}
                            {s.description && (
                                <p style={{ whiteSpace: 'pre-wrap', margin: '4px 0' }}>
                                    {s.description}
                                </p>
                            )}
                            {s.videoUrl ? (
                                <video
                                    src={s.videoUrl}
                                    controls
                                    style={{ width: '100%', borderRadius: 4, marginTop: 6 }}
                                />
                            ) : s.videoFile ? (
                                <p style={{ marginTop: 6, fontStyle: 'italic' }}>
                                    Video selected: {s.videoFile.name} (will upload on save)
                                </p>
                            ) : (
                                <p style={{ color: '#a00', marginTop: 6 }}>No video selected</p>
                            )}
                        </>
                    ) : (
                        <>
                            {/* non-video rendering */}
                            {s.featureImageUrl && (
                                <img
                                    src={s.featureImageUrl}
                                    alt=""
                                    style={{ width: '100%', borderRadius: 4, marginBottom: 6 }}
                                />
                            )}
                            {s.heading && <h4 style={{ margin: '4px 0' }}>{s.heading}</h4>}
                            {s.subheading && <h5 style={{ margin: '4px 0' }}>{s.subheading}</h5>}

                            {Array.isArray(s.items) && s.items.length > 0 ? (
                                <ul style={{ paddingLeft: 18, margin: '6px 0' }}>
                                    {s.items.map((it, idx) => (
                                        <li key={idx}>{it}</li>
                                    ))}
                                </ul>
                            ) : s.linkUrl ? (
                                <p style={{ marginTop: 6 }}>
                                    <a href={s.linkUrl} target="_blank" rel="noreferrer">
                                        {s.linkTitle || s.linkUrl}
                                    </a>
                                </p>
                            ) : s.text ? (
                                <p style={{ marginTop: 6, whiteSpace: 'pre-wrap' }}>{s.text}</p>
                            ) : null}
                        </>
                    )}

                    <div style={{ display: 'flex', gap: 8, marginTop: 8, flexWrap: 'wrap' }}>
                        <button onClick={() => moveUp(i)} disabled={i === 0}>
                            ↑ Move Up
                        </button>
                        <button onClick={() => moveDown(i)} disabled={i === sections.length - 1}>
                            ↓ Move Down
                        </button>
                        <button onClick={() => editSection(i)}>Edit</button>
                        <button onClick={() => removeSection(i)}>Remove</button>
                    </div>
                </div>
            ))}

            <hr />

            {/* Content type selector */}
            <div style={{ margin: '8px 0' }}>
                <strong>Section type:</strong>{' '}
                <label style={{ marginRight: 12 }}>
                    <input
                        type="radio"
                        checked={draftType === 'text'}
                        onChange={() => setMode('text')}
                    />{' '}
                    Text
                </label>
                <label style={{ marginRight: 12 }}>
                    <input
                        type="radio"
                        checked={draftType === 'list'}
                        onChange={() => setMode('list')}
                    />{' '}
                    List
                </label>
                <label style={{ marginRight: 12 }}>
                    <input
                        type="radio"
                        checked={draftType === 'link'}
                        onChange={() => setMode('link')}
                    />{' '}
                    Link
                </label>
                <label>
                    <input
                        type="radio"
                        checked={draftType === 'video'}
                        onChange={() => setMode('video')}
                    />{' '}
                    Video
                </label>
            </div>

            {/* Draft inputs: video */}
            {draftType === 'video' && (
                <>
                    <input
                        type="text"
                        placeholder="Video title (optional)"
                        value={videoTitle}
                        onChange={(e) => setVideoTitle(e.target.value)}
                        className="muulti-input"
                    />

                    <textarea
                        placeholder="Video description (optional)"
                        value={videoDescription}
                        onChange={(e) => setVideoDescription(e.target.value)}
                        className="muulti-textarea"
                    />

                    {/* Existing video preview (when editing) */}
                    {videoUrlExisting && !videoMarkedClear && !videoFile && (
                        <div style={{ margin: '6px 0' }}>
                            <video
                                src={videoUrlExisting}
                                controls
                                style={{ width: '100%', borderRadius: 4 }}
                            />
                            <div style={{ marginTop: 6 }}>
                                <button onClick={() => setVideoMarkedClear(true)}>
                                    Remove existing video
                                </button>
                            </div>
                        </div>
                    )}

                    {/* New video file chooser */}
                    <input
                        type="file"
                        accept="video/*"
                        onChange={(e) => {
                            setVideoFile(e.target.files?.[0] || null);
                            // if picking a new one, we’re effectively replacing, so clear url flag
                            if (e.target.files?.[0]) {
                                setVideoMarkedClear(false);
                            }
                        }}
                        className="muulti-input"
                    />

                    {/* If user cleared existing but didn't pick a new file, show note */}
                    {videoMarkedClear && !videoFile && (
                        <p style={{ color: '#a00', marginTop: 6 }}>
                            Existing video removed. Pick a new file or save without a video.
                        </p>
                    )}
                </>
            )}

            {/* Draft inputs: text/list/link */}
            {draftType !== 'video' && (
                <>
                    <input
                        type="text"
                        placeholder={draftType === 'link' ? 'Link Title (optional)' : 'Heading (optional)'}
                        value={h}
                        onChange={(e) => setH(e.target.value)}
                        className="muulti-input"
                    />
                    <input
                        type="text"
                        placeholder="Subheading (optional)"
                        value={sh}
                        onChange={(e) => setSH(e.target.value)}
                        className="muulti-input"
                    />

                    <textarea
                        placeholder={
                            draftType === 'list'
                                ? 'List items (one per line)'
                                : draftType === 'link'
                                ? 'https://example.com'
                                : 'Body text (optional)'
                        }
                        value={body}
                        onChange={(e) => setBody(e.target.value)}
                        className="muulti-textarea"
                    />

                    {/* Existing image preview (when editing) */}
                    {imgUrlExisting && !imgMarkedClear && !imgFile && (
                        <div style={{ margin: '6px 0' }}>
                            <img
                                src={imgUrlExisting}
                                alt="Feature"
                                style={{ width: '100%', borderRadius: 4 }}
                            />
                            <div style={{ marginTop: 6 }}>
                                <button onClick={() => setImgMarkedClear(true)}>
                                    Remove existing image
                                </button>
                            </div>
                        </div>
                    )}

                    <input
                        type="file"
                        accept="image/*"
                        onChange={(e) => {
                            setImgFile(e.target.files?.[0] || null);
                            // if picking a new one, we’re replacing, so clear url flag
                            if (e.target.files?.[0]) {
                                setImgMarkedClear(false);
                            }
                        }}
                        className="muulti-input"
                    />
                    {imgPreview && (
                        <img
                            src={imgPreview}
                            alt="Preview"
                            style={{ width: '100%', margin: '8px 0', borderRadius: 4 }}
                        />
                    )}
                    {imgMarkedClear && !imgFile && (
                        <p style={{ color: '#a00', marginTop: 6 }}>
                            Existing image removed. Pick a new image or save without one.
                        </p>
                    )}
                </>
            )}

            <div style={{ display: 'flex', gap: 8, marginTop: 8, flexWrap: 'wrap' }}>
                <button onClick={addSection} disabled={!hasDraft}>
                    {editingIndex != null ? 'Save Changes' : 'Add Section'}
                </button>
                {editingIndex != null && (
                    <button onClick={resetDraft}>Cancel Edit</button>
                )}
            </div>

            <hr />

            {/* Display mode selector */}
            <div className="muulti-field">
                <label className="muulti-label">Display</label>
                <select
                    className="muulti-input"
                    value={displayMode}
                    onChange={(e) => onDisplayModeChange?.(e.target.value)}
                >
                    <option value="full">Full</option>
                    <option value="compressed">Compressed</option>
                </select>
            </div>

            <div className="muulti-container-button-container">
                <button
                    onClick={handleSavePanel}
                    className="muulti-button"
                    disabled={!hasAnyData}
                >
                    Save Panel
                </button>

                {enablePublishButton && (
                    <button
                        onClick={handleSaveAndPublish}
                        className="muulti-button"
                        disabled={!hasAnyData}
                    >
                        Save Panel &amp; Publish
                    </button>
                )}

                <button onClick={onCancel} className="muulti-button">
                    Cancel
                </button>
            </div>
        </div>
    );
}
